import React, { useState, useCallback, useMemo } from 'react';
import { ImportantNote, ResidentVisitation } from '../types';
import ConfirmModal from '../../../components/ConfirmModal';

type WriteResult = { success: true } | { success: false; error: string };

interface ImportantInformationSectionProps {
  importantNotes: ImportantNote[];
  residentVisitations: ResidentVisitation[];
  userRole: 'staff' | 'family';
  onSaveEdit: (id: number, notes: string) => Promise<WriteResult>;
  onUnflag: (id: number) => Promise<WriteResult>;
  onMarkCompleted: (id: number) => Promise<WriteResult>;
}

// =====================================================================
// Inline icons
// =====================================================================

function StarIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-6 text-accent-yellow"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
  );
}

// =====================================================================
// Helpers (pure)
// =====================================================================

/**
 * Order: Scheduled before Completed; among Scheduled ascending by
 * scheduled_date (soonest first); among Completed descending by
 * scheduled_date (most recent first). Sort is stable on a copy so the
 * source array passed via props is never mutated.
 */
function sortVisitations(list: ResidentVisitation[]): ResidentVisitation[] {
  return [...list].sort((a, b) => {
    if (a.status !== b.status) {
      return a.status === 'Scheduled' ? -1 : 1;
    }
    const aTime = new Date(a.scheduled_date).getTime();
    const bTime = new Date(b.scheduled_date).getTime();
    if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
    if (Number.isNaN(aTime)) return 1;
    if (Number.isNaN(bTime)) return -1;
    return a.status === 'Scheduled' ? aTime - bTime : bTime - aTime;
  });
}

function formatScheduledDate(raw: string): string {
  if (!raw) return '';
  // Treat YYYY-MM-DD as a local date (no UTC drift).
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  let d: Date;
  if (ymd) {
    d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
  } else {
    d = new Date(raw);
  }
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// =====================================================================
// Component
// =====================================================================

const ImportantInformationSection: React.FC<ImportantInformationSectionProps> = ({
  importantNotes,
  residentVisitations,
  userRole,
  onSaveEdit,
  onUnflag,
  onMarkCompleted,
}) => {
  const isStaff = userRole === 'staff';

  // Per-row UI state (Tasks 9.3, 9.7).
  const [editingIds, setEditingIds] = useState<Set<number>>(() => new Set());
  const [editingDrafts, setEditingDrafts] = useState<Map<number, string>>(() => new Map());
  const [savingNoteIds, setSavingNoteIds] = useState<Set<number>>(() => new Set());
  const [markingIds, setMarkingIds] = useState<Set<number>>(() => new Set());
  const [noteErrors, setNoteErrors] = useState<Map<number, string>>(() => new Map());
  const [visitErrors, setVisitErrors] = useState<Map<number, string>>(() => new Map());

  // Confirm-modal state for Remove Important (Task 9.4).
  const [confirmingUnflagId, setConfirmingUnflagId] = useState<number | null>(null);

  // ---------------------------------------------------------------
  // Edit affordance
  // ---------------------------------------------------------------

  const beginEdit = useCallback((note: ImportantNote) => {
    setEditingIds((prev) => {
      const next = new Set(prev);
      next.add(note.id);
      return next;
    });
    setEditingDrafts((prev) => {
      const next = new Map(prev);
      next.set(note.id, note.notes);
      return next;
    });
    // Clear any prior error for this row when retrying.
    setNoteErrors((prev) => {
      if (!prev.has(note.id)) return prev;
      const next = new Map(prev);
      next.delete(note.id);
      return next;
    });
  }, []);

  const updateDraft = useCallback((id: number, text: string) => {
    setEditingDrafts((prev) => {
      const next = new Map(prev);
      next.set(id, text);
      return next;
    });
  }, []);

  const cancelEdit = useCallback((id: number) => {
    setEditingIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setEditingDrafts((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const saveEdit = useCallback(
    async (id: number) => {
      const draft = editingDrafts.get(id) ?? '';
      // Clear prior error and mark saving.
      setNoteErrors((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
      setSavingNoteIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      const result = await onSaveEdit(id, draft);
      setSavingNoteIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      if (result.success) {
        // Exit edit mode; parent will refresh notes via fetchPatientData.
        setEditingIds((prev) => {
          if (!prev.has(id)) return prev;
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        setEditingDrafts((prev) => {
          if (!prev.has(id)) return prev;
          const next = new Map(prev);
          next.delete(id);
          return next;
        });
      } else {
        setNoteErrors((prev) => {
          const next = new Map(prev);
          next.set(id, result.error);
          return next;
        });
      }
    },
    [editingDrafts, onSaveEdit],
  );

  // ---------------------------------------------------------------
  // Unflag affordance
  // ---------------------------------------------------------------

  const requestUnflag = useCallback((id: number) => {
    // Clear any prior error for this row when retrying.
    setNoteErrors((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
    setConfirmingUnflagId(id);
  }, []);

  const cancelUnflag = useCallback(() => {
    setConfirmingUnflagId(null);
  }, []);

  const confirmUnflag = useCallback(async () => {
    const id = confirmingUnflagId;
    if (id == null) return;
    const result = await onUnflag(id);
    setConfirmingUnflagId(null);
    if (!result.success) {
      setNoteErrors((prev) => {
        const next = new Map(prev);
        next.set(id, result.error);
        return next;
      });
    }
    // On success, parent refreshes list via fetchPatientData; row will
    // disappear when re-rendered with new props.
  }, [confirmingUnflagId, onUnflag]);

  // ---------------------------------------------------------------
  // Mark visit completed affordance
  // ---------------------------------------------------------------

  const handleMarkCompleted = useCallback(
    async (id: number) => {
      // Clear any prior error for this row when retrying.
      setVisitErrors((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
      setMarkingIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      const result = await onMarkCompleted(id);
      setMarkingIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      if (!result.success) {
        setVisitErrors((prev) => {
          const next = new Map(prev);
          next.set(id, result.error);
          return next;
        });
      }
      // On success, parent refreshes list via fetchPatientData.
    },
    [onMarkCompleted],
  );

  const sortedVisitations = useMemo(
    () => sortVisitations(residentVisitations),
    [residentVisitations],
  );

  // ---------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------

  return (
    <div className="p-6 bg-white rounded-2xl shadow-sm">
      <h3 className="text-xl font-bold text-primary mb-4 pb-4 border-b border-light-gray flex items-center gap-3">
        <StarIcon />
        Important Information
      </h3>

      <div className="space-y-6">
        {/* ----------------------------- */}
        {/* Important Notes subcard       */}
        {/* ----------------------------- */}
        <section className="bg-white rounded-2xl border border-light-gray shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-light-gray bg-gray-50/50">
            <h4 className="text-sm font-semibold text-primary">Important Notes</h4>
          </div>
          <div className="divide-y divide-light-gray">
            {importantNotes.length === 0 ? (
              <div className="p-4 text-sm text-gray-500">No important notes.</div>
            ) : (
              importantNotes.map((note) => {
                const isEditing = editingIds.has(note.id);
                const draft = editingDrafts.get(note.id) ?? note.notes;
                const isSaving = savingNoteIds.has(note.id);
                const error = noteErrors.get(note.id);
                return (
                  <div key={note.id} className="p-4">
                    {isEditing ? (
                      <div className="space-y-2">
                        <textarea
                          value={draft}
                          onChange={(e) => updateDraft(note.id, e.target.value)}
                          rows={3}
                          className="w-full rounded-md border border-light-gray px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                          aria-label="Edit note text"
                          disabled={isSaving}
                        />
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => saveEdit(note.id)}
                            disabled={isSaving}
                            className="px-3 py-1.5 rounded-md bg-primary text-white text-sm font-medium shadow-sm hover:bg-primary/90 disabled:opacity-60"
                          >
                            {isSaving ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            type="button"
                            onClick={() => cancelEdit(note.id)}
                            disabled={isSaving}
                            className="px-3 py-1.5 rounded-md bg-light-gray text-gray-700 text-sm font-medium hover:bg-gray-200 disabled:opacity-60"
                          >
                            Cancel
                          </button>
                        </div>
                        {error && (
                          <p className="text-xs text-red-600 mt-1">{error}</p>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-800 whitespace-pre-wrap break-words">
                            {note.notes}
                          </p>
                          <p className="text-xs text-gray-500 mt-2">
                            Recorded {note.recorded_at}
                          </p>
                          <p className="text-xs text-gray-500">
                            by {note.creator_name}
                          </p>
                        </div>
                        {isStaff && (
                          <div className="flex flex-col items-start md:items-end gap-1 flex-shrink-0">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => beginEdit(note)}
                                className="p-1.5 rounded-md bg-light-gray text-gray-700 hover:bg-gray-200"
                                aria-label="Edit note"
                                title="Edit"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => requestUnflag(note.id)}
                                className="p-1.5 rounded-md bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"
                                aria-label="Remove from important"
                                title="Remove Important"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l1.664 1.664M21 21l-1.5-1.5m-5.533-1.533l-1.109-1.109a1.5 1.5 0 00-2.122 0L5.5 21.096V4.5a2 2 0 012-2h9.258M6.228 6.228L3.934 3.934" />
                                </svg>
                              </button>
                            </div>
                            {error && (
                              <p className="text-xs text-red-600 mt-1">{error}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </section>

        {/* ----------------------------- */}
        {/* Visitations subcard           */}
        {/* ----------------------------- */}
        <section className="bg-white rounded-2xl border border-light-gray shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-light-gray bg-gray-50/50">
            <h4 className="text-sm font-semibold text-primary">Visitations</h4>
          </div>
          <div className="divide-y divide-light-gray">
            {sortedVisitations.length === 0 ? (
              <div className="p-4 text-sm text-gray-500">No visitations.</div>
            ) : (
              sortedVisitations.map((visit) => {
                const isMarking = markingIds.has(visit.id);
                const error = visitErrors.get(visit.id);
                const badgeClass =
                  visit.status === 'Scheduled'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-700';
                const showNotes =
                  visit.notes !== null && visit.notes.trim() !== '';
                const canMark = isStaff && visit.status === 'Scheduled';
                return (
                  <div key={visit.id} className="p-4">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-gray-800">
                            {formatScheduledDate(visit.scheduled_date)}
                          </span>
                          <span className="sr-only">{visit.scheduled_date}</span>
                          <span className="text-gray-600">
                            {visit.scheduled_time_slot}
                          </span>
                          <span
                            className={`text-xs font-medium px-2 py-0.5 rounded-full ${badgeClass}`}
                          >
                            {visit.status}
                          </span>
                        </div>
                        {showNotes && (
                          <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap break-words">
                            Notes: {visit.notes}
                          </p>
                        )}
                      </div>
                      {canMark && (
                        <div className="flex flex-col items-start md:items-end gap-1 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => handleMarkCompleted(visit.id)}
                            disabled={isMarking}
                            className="px-3 py-1.5 rounded-md bg-primary text-white text-sm font-medium shadow-sm hover:bg-primary/90 disabled:opacity-60"
                          >
                            {isMarking ? 'Updating…' : 'Mark as Completed'}
                          </button>
                          {error && (
                            <p className="text-xs text-red-600 mt-1">{error}</p>
                          )}
                        </div>
                      )}
                      {!canMark && error && (
                        <p className="text-xs text-red-600 mt-1">{error}</p>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>
      </div>

      <ConfirmModal
        modalText="Remove the Important flag from this note?"
        onModalConfirm={confirmUnflag}
        modalOpenFlag={confirmingUnflagId !== null}
        toggleModalClose={cancelUnflag}
        className=""
      />
    </div>
  );
};

export default ImportantInformationSection;
